import type { AwardCheck, DocumentView } from '@myrewards/contracts';
import { useState, type ReactElement } from 'react';
import { Link, ToggleButton, ToggleButtonGroup, type Key } from 'react-aria-components';
import { useQuery } from '@tanstack/react-query';
import { awardTaxQuery, resnapshot, setCostType } from '../../api/ops';
import {
  Button,
  Card,
  ConfirmDialog,
  cx,
  DataTable,
  EmptyState,
  formatCount,
  formatDate,
  formatMoney,
  plural,
  scoped,
  StatusPill,
} from '../../ui';
import { amountWords, costTypeWord, problemWords, TYPE_LABEL } from '../labels';
import { TaxEntryTable } from '../tax/TaxEntryRow';
import { AttachButton, FileList, Notice } from '../shared';
import { paidFrom } from './presenter';
import { useCheckCommand } from './useCheckCommand';
import s from '../Ops.module.css';

type Line = AwardCheck['lines'][number];

function personLine(line: Line): string {
  return [
    line.person.homeOrg === null ? null : `Home org ${line.person.homeOrg}`,
    line.person.manager === null ? null : `manager ${line.person.manager}`,
    line.costTypeSwitched
      ? `${costTypeWord(line.costType)} (switched at the check)`
      : `${costTypeWord(line.costType)} (from the directory)`,
  ]
    .filter((part): part is string => part !== null)
    .join(' · ');
}

// --- Lines, FMV and Paid from ------------------------------------------------------------------------

export function LinesCard({
  check,
  canChange,
}: {
  check: AwardCheck;
  canChange: boolean;
}): ReactElement {
  const [only] = check.lines;
  const single = check.lines.length === 1 && only !== undefined;
  const fmvNeeded = check.checklist.find((c) => c.id === 'fmv')?.status !== 'not_needed';
  const pools = paidFrom(check.lines);
  const [confirming, setConfirming] = useState(false);
  const retake = useCheckCommand(check.award.id, (reason: string) =>
    resnapshot(check.award.id, { version: check.award.version, reason }),
  );
  const fmv = check.fmv;
  // APP-151: a PAA since APP-151 counts its options in the CFO's value of one option.
  const option = check.optionValue;
  const fmvChanged = !fmv.matches && fmv.current !== null;
  const optionChanged = option !== null && !option.matches && option.current !== null;
  return (
    <Card
      title={single ? 'Line' : `Lines · ${formatCount(check.lines.length)}`}
      helpAnchor="check-lines"
      help={{
        shows:
          'Each person on the award with the amounts it was approved with, the FMV its options use, and the pool that pays.',
        canDo:
          'Check every person and amount. If the FMV or the value of one option changed after it was sent, take the values again with a reason, or keep the ones it was sent with.',
      }}
      aside={<StatusPill tone="info" size="sm" label="With the Stock Admin team" />}
    >
      {check.lines.length === 0 ? (
        <EmptyState
          variant="first-day"
          art="award"
          size="sm"
          headline="No one on this award"
          body="An award reaches the check with at least one person. Open its record to see what happened."
        />
      ) : single ? (
        <>
          <div className={s.personHead}>
            <span className={cx(s.avatar, s.avatarLg)} aria-hidden="true">
              {only.person.name
                .split(' ')
                .map((p) => p[0] ?? '')
                .join('')
                .slice(0, 2)}
            </span>
            <span className={s.personText}>
              <span className={s.personName}>
                {only.person.name}
                {only.person.eeNumber !== null && ` · EE# ${only.person.eeNumber}`}
              </span>
              <span className={s.personDetail}>{personLine(only)}</span>
            </span>
          </div>
          <div className={s.tiles}>
            {only.totalValueCents !== null && (
              <div className={s.tile}>
                <p className={cx('eyebrow', s.tileLabel)}>
                  {scoped(only.person.name, 'total value')}
                </p>
                <p className={cx('disp', s.tileValue)}>{formatMoney(only.totalValueCents)}</p>
                <p className={s.tileNote}>As approved</p>
              </div>
            )}
            <div className={cx(s.tile, s.tileCyan)}>
              <p className={cx('eyebrow', s.tileLabel)}>{scoped(only.person.name, 'cash')}</p>
              <p className={cx('disp', s.tileValue)}>{formatMoney(only.cashCents)}</p>
              <p className={s.tileNote}>
                {check.award.type === 'PAA' && only.cashCents > 0
                  ? 'The person chooses (a) to (d)'
                  : 'Paid through Payroll'}
              </p>
            </div>
            {only.options > 0 && (
              <div className={cx(s.tile, s.tileGold)}>
                <p className={cx('eyebrow', s.tileLabel)}>{scoped(only.person.name, 'options')}</p>
                <p className={cx('disp', s.tileValue)}>
                  {plural(only.options, 'option', 'options')}
                </p>
                <p className={s.tileNote}>
                  {option === null
                    ? `At ${formatMoney(fmv.used.cents)} per share, whole options, rounded down`
                    : `At ${formatMoney(option.used.cents)} an option, whole options, rounded down`}
                </p>
              </div>
            )}
          </div>
        </>
      ) : (
        <DataTable
          caption="People on this award"
          minWidth={760}
          cards
          rows={check.lines}
          rowKey={(line) => line.lineId}
          empty={{ title: 'No one on this award.', body: 'Open its record to see what happened.' }}
          columns={[
            {
              id: 'person',
              header: 'Person',
              card: 'title',
              cell: (line) => (
                <>
                  <span className={s.strong}>{line.person.name}</span>
                  <span className={s.sub}>
                    {line.person.eeNumber === null ? 'No EE# yet' : `EE# ${line.person.eeNumber}`}
                  </span>
                </>
              ),
            },
            {
              id: 'org',
              header: 'Home org · manager',
              cell: (line) =>
                [
                  line.person.homeOrg ?? 'No home org',
                  line.person.manager ?? 'no manager listed',
                ].join(' · '),
            },
            {
              id: 'cash',
              header: 'Cash',
              numeric: true,
              cell: (line) => formatMoney(line.cashCents),
            },
            {
              id: 'options',
              header: 'Options',
              numeric: true,
              cell: (line) => (line.options === 0 ? 'Cash only' : formatCount(line.options)),
            },
            { id: 'pool', header: 'Paid from', cell: (line) => line.pool.name },
          ]}
        />
      )}
      <div className={s.split}>
        <div>
          {fmvNeeded ? (
            <>
              <p className={s.strong}>FMV used · {formatMoney(fmv.used.cents)} per share</p>
              <p className={s.muted}>
                {fmv.used.effectiveFrom === null
                  ? 'One FMV for every award'
                  : `Effective ${formatDate(fmv.used.effectiveFrom)} · one FMV for every award`}
              </p>
              {option !== null && (
                <>
                  <p className={s.strong}>Value of one option · {formatMoney(option.used.cents)}</p>
                  <p className={s.muted}>
                    {option.used.effectiveFrom === null
                      ? 'The CFO’s value, for this PAA’s options'
                      : `Effective ${formatDate(option.used.effectiveFrom)} · the CFO’s value, for this PAA’s options`}
                  </p>
                </>
              )}
              {fmvChanged && fmv.current !== null && (
                <Notice tone="waiting">
                  The FMV is {formatMoney(fmv.current.cents)} from{' '}
                  {formatDate(fmv.current.effectiveFrom)} now. Take the values again to work this
                  award out at today’s FMV, or keep the values it was sent with.
                </Notice>
              )}
              {optionChanged && option.current !== null && (
                <Notice tone="waiting">
                  The value of one option is {formatMoney(option.current.cents)} from{' '}
                  {formatDate(option.current.effectiveFrom)} now. Take the values again to work this
                  award out at today’s values, or keep the values it was sent with.
                </Notice>
              )}
              {(fmvChanged || optionChanged) && canChange && (
                <Button
                  variant="ghost"
                  size="sm"
                  onPress={() => {
                    setConfirming(true);
                  }}
                >
                  Take the values again
                </Button>
              )}
              {retake.error !== null && <Notice tone="problem">{retake.error}</Notice>}
            </>
          ) : (
            <>
              <p className={s.strong}>No options on this award</p>
              <p className={s.muted}>The FMV doesn’t apply: it pays cash only.</p>
            </>
          )}
        </div>
        <div>
          <p className={s.strong}>Paid from</p>
          <ul className={s.paidFrom}>
            {pools.map((row) => (
              <li key={row.pool.id} className={s.paidRow}>
                <span>
                  <span className={s.strong}>{row.pool.name}</span>
                  <span className={s.muted}> · {plural(row.people, 'person', 'people')}</span>
                </span>
                <span className={s.strong}>{amountWords(row.cashCents, row.options)}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      {(fmv.current !== null || optionChanged) && (
        <ConfirmDialog
          isOpen={confirming}
          onOpenChange={setConfirming}
          title="Take the values again?"
          intro={`The amounts are worked out again at today’s ${option === null ? 'FMV' : 'values'} and settings. Pools and any OH or G&A switches stay. If that would go over budget, nothing changes.`}
          summary={[
            ...(fmv.current === null
              ? []
              : [
                  {
                    label: 'FMV per share',
                    before: formatMoney(fmv.used.cents),
                    value: formatMoney(fmv.current.cents),
                  },
                ]),
            ...(option === null || option.current === null
              ? []
              : [
                  {
                    label: 'Value of one option',
                    before: formatMoney(option.used.cents),
                    value: formatMoney(option.current.cents),
                  },
                ]),
          ]}
          reasonLabel="Why are you taking the values again?"
          confirmLabel="Take the values again"
          onConfirm={async (reason) => {
            await retake.mutateAsync(reason).catch(() => undefined);
          }}
        />
      )}
    </Card>
  );
}

// --- Coding ----------------------------------------------------------------------------------------------

function CostTypeSwitch({ check, line }: { check: AwardCheck; line: Line }): ReactElement {
  const [target, setTarget] = useState<'OH' | 'GA' | null>(null);
  const change = useCheckCommand(
    check.award.id,
    (vars: { costType: 'OH' | 'GA'; reason: string }) =>
      setCostType(check.award.id, { version: check.award.version, lineId: line.lineId, ...vars }),
  );
  return (
    <>
      <ToggleButtonGroup
        aria-label={`Cost type for ${line.person.name}`}
        selectionMode="single"
        disallowEmptySelection
        selectedKeys={[line.costType]}
        onSelectionChange={(keys: Set<Key>) => {
          const [key] = [...keys];
          if ((key === 'OH' || key === 'GA') && key !== line.costType) setTarget(key);
        }}
        className={s.segmented}
      >
        <ToggleButton id="OH" className={s.segment}>
          OH
        </ToggleButton>
        <ToggleButton id="GA" className={s.segment}>
          G&amp;A
        </ToggleButton>
      </ToggleButtonGroup>
      {change.error !== null && <Notice tone="problem">{change.error}</Notice>}
      <ConfirmDialog
        isOpen={target !== null}
        onOpenChange={(open) => {
          if (!open) setTarget(null);
        }}
        title={`Switch ${line.person.name} to ${target === null ? '' : costTypeWord(target)}?`}
        intro="Only this award’s charge code changes; the directory stays as it is. The switch goes in the award’s history with your reason."
        summary={[
          {
            label: 'Cost type',
            before: costTypeWord(line.costType),
            value: target === null ? '' : costTypeWord(target),
          },
        ]}
        reasonLabel="Why is the directory wrong for this award?"
        confirmLabel="Switch the cost type"
        onConfirm={async (reason) => {
          if (target === null) return;
          await change.mutateAsync({ costType: target, reason }).catch(() => undefined);
        }}
      />
    </>
  );
}

export function CodingCard({
  check,
  canChange,
}: {
  check: AwardCheck;
  canChange: boolean;
}): ReactElement {
  return (
    <Card
      title="Coding"
      helpAnchor="check-coding"
      help={{
        shows:
          'The charge code on each person’s line: cost type (OH or G&A), account, org and project, filled in from the award type and the directory.',
        canDo:
          'When the directory has someone’s OH or G&A wrong, switch it here with a reason. Only this award’s code changes.',
      }}
    >
      {check.lines.length === 0 ? (
        <EmptyState
          variant="first-day"
          art="chart"
          size="sm"
          headline="Nothing to code"
          body="Each person’s charge code shows here once the award has people on it."
        />
      ) : (
        <ul className={s.codingList}>
          {check.lines.map((line) => (
            <li key={line.lineId} className={s.codingRow}>
              <div className={s.codingWho}>
                <span className={s.strong}>{line.person.name}</span>
                <span className={s.sub}>
                  {line.costTypeSwitched ? (
                    'Switched at the check'
                  ) : line.person.codingChanged === null ? (
                    'OH or G&A and home org from the directory · not changed since setup'
                  ) : (
                    <>
                      OH or G&amp;A and home org from the directory · last changed by{' '}
                      {line.person.codingChanged.by} on {formatDate(line.person.codingChanged.at)} ·{' '}
                      <Link
                        href={`/settings/system/people?person=${encodeURIComponent(line.person.id)}`}
                        className={s.lineLink}
                        aria-label={`History of ${line.person.name}’s home org and OH or G&A`}
                      >
                        History
                      </Link>
                    </>
                  )}
                </span>
              </div>
              <div className={s.codingType}>
                <span className={cx('eyebrow', s.fieldLabel)}>Cost type</span>
                {canChange ? (
                  <CostTypeSwitch check={check} line={line} />
                ) : (
                  <span className={s.strong}>{costTypeWord(line.costType)}</span>
                )}
              </div>
              <dl className={s.codes}>
                <div>
                  <dt className={cx('eyebrow', s.fieldLabel)}>Account</dt>
                  <dd className={s.code}>{line.coding?.account ?? 'Not coded yet'}</dd>
                </div>
                <div>
                  <dt className={cx('eyebrow', s.fieldLabel)}>Org</dt>
                  <dd className={s.code}>{line.coding?.org ?? 'Not coded yet'}</dd>
                </div>
                <div>
                  <dt className={cx('eyebrow', s.fieldLabel)}>Project</dt>
                  <dd className={s.code}>
                    {line.coding === null
                      ? 'Not coded yet'
                      : (line.coding.project ??
                        `None for ${check.award.type === 'PAA' || check.award.type === 'TAA' ? 'this pool' : 'this award'}`)}
                  </dd>
                </div>
              </dl>
            </li>
          ))}
        </ul>
      )}
      <p className={s.muted}>
        OH or G&amp;A and the home org come from the directory. Awards keep the codes they were
        worked out with.
      </p>
    </Card>
  );
}

// --- Proof and write-up ----------------------------------------------------------------------------------

export function ProofCard({
  check,
  documents,
  canChange,
}: {
  check: AwardCheck;
  documents: readonly DocumentView[];
  canChange: boolean;
}): ReactElement {
  const files = documents.filter((d) =>
    ['proof', 'write_up', 'original_ask', 'other'].includes(d.purpose),
  );
  const text = check.award.writeUpText ?? check.award.justification;
  const title = check.proof.needed === 'proof' ? 'Proof' : 'Proof & write-up';
  // QA-015: an award type that needs no proof says so here too, as its checklist item does.
  const noneNeeded = check.proof.needed === null;
  const typeName = TYPE_LABEL[check.award.type];
  const aType = `${/^[AEIOU]/.test(typeName) ? 'An' : 'A'} ${typeName}`;
  return (
    <Card
      title={title}
      help={{
        shows: noneNeeded
          ? `${aType} needs no approval proof. Its justification and anything attached to it show here, to read before its letter goes out.`
          : 'The approval proof and the write-up or justification on the award, so you can read them before its letter goes out.',
        canDo:
          'Open each file once its virus check clears it. Attach a missing file here; every change goes in the award’s history.',
      }}
      aside={
        canChange ? (
          <AttachButton
            requestId={check.award.id}
            purpose={check.proof.needed ?? 'other'}
            label="Attach a file"
          />
        ) : undefined
      }
    >
      {text !== null && text.trim() !== '' && (
        <blockquote className={s.quote}>
          <p className={cx('eyebrow', s.fieldLabel)}>
            {check.award.writeUpText ? 'Write-up' : 'Justification'}
          </p>
          <p className={s.quoteText}>{text}</p>
        </blockquote>
      )}
      <FileList
        requestId={check.award.id}
        documents={files}
        canChange={canChange}
        empty={
          text === null || text.trim() === '' ? (
            <EmptyState
              variant="first-day"
              art="letter"
              size="sm"
              headline={noneNeeded ? 'No proof needed' : 'No proof to read yet'}
              body={
                noneNeeded
                  ? `${aType} needs no approval proof. Anything attached to the award shows here.`
                  : 'The approval proof and any write-up attached to the award show here, so you can read them before its letter goes out.'
              }
            />
          ) : (
            <p className={s.muted}>No files attached; the write-up above is typed in.</p>
          )
        }
      />
      {check.proof.needed !== null && !check.proof.present && (
        <Notice tone="waiting">
          {check.proof.needed === 'proof'
            ? 'This award needs its approval proof before its letter. Attach it, and it counts once the virus check clears it.'
            : 'This award needs its write-up before its letter: typed in on the award or attached here.'}
        </Notice>
      )}
    </Card>
  );
}

// --- Tax from ADP (APP-101) ------------------------------------------------------------------------

/**
 * A PAA with cash: each person's taxes from ADP Quick Calc, in the same rows as Tax to enter. Any
 * Stock Admin can enter them here, claim or not; the letters wait until everyone has one.
 */
export function TaxCard({
  check,
  documents,
  canChange,
}: {
  check: AwardCheck;
  documents: readonly DocumentView[];
  canChange: boolean;
}): ReactElement | null {
  const taxed = check.award.type === 'PAA' && check.lines.some((l) => l.cashCents > 0);
  const tax = useQuery({ ...awardTaxQuery(check.award.id), enabled: taxed });
  if (!taxed) return null;
  const screenshots = documents.filter((d) => d.purpose === 'tax_estimate');
  return (
    <Card
      title="Tax from ADP"
      help={{
        shows:
          'Each person’s gross, the taxes and net pay from ADP Quick Calc, the screenshot and who entered them. The letter shows the figure for every choice. Always estimates.',
        canDo:
          'Type the taxes ADP shows (the net pay follows) and save the row, here or under Tax to enter. You don’t need the claim. Attach the ADP screenshot; it stays private to the team.',
      }}
      aside={<StatusPill tone="waiting" size="sm" label="Estimate" />}
    >
      {tax.data !== undefined ? (
        <TaxEntryTable
          awards={[tax.data]}
          caption={`Tax from ADP for ${check.award.displayId ?? 'this award'}`}
          showAward={false}
        />
      ) : tax.isError ? (
        <Notice tone="problem">{problemWords(tax.error)}</Notice>
      ) : (
        <p className={s.muted}>Reading each person’s tax…</p>
      )}
      <FileList
        requestId={check.award.id}
        documents={screenshots}
        canChange={canChange}
        empty={<p className={s.muted}>No ADP screenshot attached yet.</p>}
      />
    </Card>
  );
}
